import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, IQueryPageData, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';
import {
  IPartFinderCatalogFacets,
  IPartFinderCatalogFamily,
  IPartFinderFamilyDefinition,
  IPartFinderReel,
  IPartFinderReelsPage,
  IPartFinderReelsRequestPayload,
  IPartFinderSelection,
} from 'partFinder/types';

// The results grid loads the family's parts page by page, the ones the search picked first: a
// small first page, so those show quickly, then pages of 500 parts - the most one request pulls
// live inventory for - until the server has no more. Asked to (parts_cut), the server cuts a page
// where the matching parts end, so the picked parts show on their own as soon as they are in, and
// a page can be shorter than asked: each page starts where the last one ended, and the page
// parameter is the position of its first part, from 1.
const REELS_FIRST_PAGE_PARTS = 100;
const REELS_PAGE_PARTS = 500;

const getReelsPageWindow = (firstPart: number) =>
  firstPart > 1
    ? { offset: firstPart - 1, limit: REELS_PAGE_PARTS }
    : { offset: 0, limit: REELS_FIRST_PAGE_PARTS };

// The next page's parameter: the position after the parts the last page reached, or none.
export const getNextReelsPage = (lastPage: IQueryPageData): number | undefined => {
  const { partsEnd } = (lastPage as GetManyResponse<IPartFinderReel, IPartFinderReelsPage>)
    .extraData ?? { partsEnd: 0 };

  return lastPage.hasMore && partsEnd > 0 ? partsEnd + 1 : undefined;
};

export const getPartFinderCatalogFamilies = async (
  requestPayload?: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartFinderCatalogFamily>> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-search/families?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IPartFinderCatalogFamily>(data?.data, data?.recordsFiltered);
};

export const getPartFinderCatalogFacets = async (
  signal?: AbortSignal
): Promise<IPartFinderCatalogFacets> => {
  const { data } = await partfinitiClient.get('/part_v2/finder-search/facets', signal);

  return objectKeysSnakeCaseToCamelCase(data) as IPartFinderCatalogFacets;
};

const getSelectionParams = (
  familyId: number,
  { values, numbers, locationIds, inStock, manufacturerIds }: IPartFinderSelection
) => {
  const params = new URLSearchParams({ family_id: String(familyId) });

  Object.entries(values).forEach(([attributeId, picked]) =>
    picked.forEach((value) => params.append(`selection[${attributeId}][]`, value))
  );
  Object.entries(numbers).forEach(([attributeId, number]) =>
    params.append(`numbers[${attributeId}]`, number)
  );

  locationIds.forEach((id) => params.append('location_ids[]', String(id)));
  manufacturerIds.forEach((id) => params.append('manufacturer_ids[]', String(id)));

  if (inStock) {
    params.append('in_stock', '1');
  }

  return params;
};

export const getPartFinderFamilyDefinition = async (
  familyId: number,
  selection: IPartFinderSelection,
  signal?: AbortSignal
): Promise<IPartFinderFamilyDefinition> => {
  const params = getSelectionParams(familyId, selection);

  const { data } = await partfinitiClient.get(`/part_v2/finder-search/family?${params}`, signal);

  return objectKeysSnakeCaseToCamelCase(data) as IPartFinderFamilyDefinition;
};

export const getPartFinderReels = async (
  { familyId, selection, page = 1 }: IPartFinderReelsRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartFinderReel, IPartFinderReelsPage>> => {
  // The family's reels come back whatever the filter page picked: the picked locations,
  // manufacturers, attribute values and numbers become grid filters the user can lift
  // (PreAppliedFilters). The server is told what was picked so it reads those parts first, in
  // the first pages. In stock still narrows: the grid has no column to show it on.
  const params = getSelectionParams(familyId, selection);
  const { offset, limit } = getReelsPageWindow(page);

  params.append('matched_first', '1');
  // Only a grid that starts the next page from parts_end may ask for the cut: a bundle paging by
  // fixed offsets would skip the parts between the cut and its next offset.
  params.append('parts_cut', '1');
  params.append('parts_offset', String(offset));
  params.append('parts_limit', String(limit));

  const { data } = await partfinitiClient.get(`/part_v2/finder-search/reels?${params}`, signal);

  // No count: the rows are reels while the pages are parts, so whether another page follows is
  // the server's to say (has_more).
  return mapRecordsFromServer<IPartFinderReel, IPartFinderReelsPage>(
    data?.data ?? [],
    undefined,
    undefined,
    { ...data, data: undefined },
    Boolean(data?.has_more)
  );
};
